import React, { useEffect, useState } from "react";
import httpClient, { TimeoutError } from "../../utils/httpClient";
import { useData } from "../../providers/DataProvider";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";

type PayrollFileCategory = "Download" | "Upload";

interface PayrollFileRecord {
  id: number;
  fileName: string;
  createdOn: string;
  createdBy: string;
  filePath: string;
  fileCategory: PayrollFileCategory;
}

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PayrollFilesPage: React.FC = () => {
  const { role } = useData();
  const { accounts } = useMsal();
  const navigate = useNavigate();

  const [files, setFiles] = useState<PayrollFileRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PayrollFileCategory>("Download");

  const roleMap: Record<string, string> = {
    "DCCM-Review": "DCCM",
    "SPM-Review": "SPM",
    "VP-Review": "VP",
    "DM-Review": "DM",
  };

  const roles = [
    ...new Set(
      ((accounts[0]?.idTokenClaims?.roles as string[]) || []).map(
        (role) => roleMap[role] || role
      )
    ),
  ].sort();

  const isPayroll = roles?.includes("Payroll");


  useEffect(() => {
    const selectedRole = localStorage.getItem("selectedRole") || "";
    if (!(selectedRole === "Payroll" || selectedRole === "Ops")) {
      navigate("/action-items");
      return;
    }
  }, [navigate]);

  useEffect(() => {
    if (accounts[0].idTokenClaims?.roles && !isPayroll) {
      navigate("/");
    }
  }, [isPayroll, accounts, navigate]);

  const loadFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await httpClient.get<PayrollFileRecord[]>("/api/payroll/files", {
        headers: {
          roleType: role,
        },
      });
      if (res && res.data) {
        setFiles(res.data);
      }
    } catch (err) {
      console.error("Failed to load payroll files:", err);
      setError("Failed to load payroll files. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = async (file: PayrollFileRecord) => {
    try {
      const kind = file.fileCategory === "Download" ? "export" : "import";
      const response = await httpClient.get<ArrayBuffer>(
        "/api/payroll/files/download",
        {
          params: { id: file.id, kind },
          responseType: "blob",
          headers: {
            roleType: role,
          },
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.fileName || "payroll-file.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to download payroll file:", err);
      alert("Failed to download file. Please try again.");
    }
  };

  const downloadFiles = files.filter((f) => f.fileCategory === "Download");
  const uploadFiles = files.filter((f) => f.fileCategory === "Upload");

  const currentFiles =
    activeTab === "Download" ? downloadFiles : uploadFiles;

  return (
    <main className="flex-grow p-4">
      <div className="bg-white shadow-xl rounded-lg overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <div>
              <h2 className="text-xl font-semibold">Payroll File Logs</h2>
              <p className="text-sm text-gray-600 mt-1">
                View history of downloaded payroll reports and uploaded payroll spreadsheets.
              </p>
            </div>
          </div>

          <div className="flex border-b border-gray-200">
            <button
              type="button"
              onClick={() => setActiveTab("Download")}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === "Download"
                ? "border-primary-600 text-primary-700"
                : "border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300"
                }`}
            >
              Downloaded Files
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("Upload")}
              className={`ml-4 px-4 py-2 text-sm font-medium border-b-2 ${activeTab === "Upload"
                ? "border-primary-600 text-primary-700"
                : "border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300"
                }`}
            >
              Uploaded Files
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <CircularProgress />
          </div>
        ) : error ? (
          <div className="p-4 text-red-600 text-sm">{error}</div>
        ) : (
          <div className="p-4">
            {currentFiles.length === 0 ? (
              <p className="text-sm text-gray-600">
                {activeTab === "Download"
                  ? "No downloaded reports found."
                  : "No uploaded files found."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        File Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        {activeTab === "Download" ? "Downloaded By" : "Uploaded By"}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        {activeTab === "Download" ? "Downloaded On" : "Uploaded On"}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentFiles.map((file) => (
                      <tr
                        key={`${file.fileCategory.toLowerCase()}-${file.id}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {file.fileName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {file.createdBy}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {formatDateTime(file.createdOn)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button
                            onClick={() => handleDownload(file)}
                            className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${file.fileCategory === "Download"
                              ? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
                              : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
                              }`}
                            aria-label={`Download ${file.fileName}`}
                          >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
};

export default PayrollFilesPage;

